import 'photoswipe/style.css';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';

import { ApolloProvider } from '@apollo/client';
import { ContentfulLivePreviewProvider } from '@contentful/live-preview/react';
import { GoogleTagManager } from '@next/third-parties/google';
import { SaleorAuthProvider } from '@saleor/auth-sdk/react';
import type { NextPage, NextPageContext } from 'next';
import type { AppContext, AppProps } from 'next/app';
import App from 'next/app';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { SnackbarProvider } from 'notistack';
import { type ReactElement, type ReactNode, useEffect, useState } from 'react';
import { ThemeProvider } from 'styled-components';

import { loadAriaLabels } from '@/utils/ariaLabels';
import {
  extractPageUrlFromNextContext,
  parseUrlOrigin,
  removeTrailingSlash,
} from '@/utils/urlUtils';

import SkipToContentLink from '@/components/A11y/SkipToContentLink';
import { CampaignTrackerInit } from '@/components/CampaignTrackerInit';
import ErrorBoundaryWrapper from '@/components/ErrorBoundary';
import { FaviconLinks } from '@/components/FaviconLinks/FaviconLinks';
import { NextProgressWrapper } from '@/components/NextProgressWrapper';
import { Processing } from '@/components/Processing/Processing';
import SEOHead from '@/components/SEOHead/SEOHead';
import { saleorAuthClient } from '@/graphql/client';
import type { ContentfulSeoConfiguration } from '@/graphql/contentful/api';
import { useApollo } from '@/hooks/useApollo';
import { useAriaLabels } from '@/hooks/useAriaLabels';
import { useBuildIdGuard } from '@/hooks/useBuildIdGuard';
import { useRouter } from '@/hooks/useRouter';
import { useSearchInsights } from '@/hooks/useSearchInsightsInit';
import { GlobalEScript } from '@/integrations/global-e/GlobalEScript';
import { DeferredGoogleTagManager } from '@/integrations/gtm/DeferredGoogleTagManager';
import { OneTrustScript } from '@/integrations/onetrust/OnetrustScript';
import { OneTrustScriptReloader } from '@/integrations/onetrust/OneTrustScriptReloader';
import { YourAssetScript } from '@/integrations/YourAsset/YourAsset';
import { ZipScript } from '@/integrations/zip/ZipScript';
import applyGoogleTranslateDOMPatch from '@/lib/applyGoogleTranslateDOMPatch';
import {
  Brand,
  LocaleConfiguration,
  toBrandEnum,
} from '@/lib/configuration/configuration';
import {
  API_URI,
  BRAND,
  CONFIGURATION_APP_API_URI,
  GLOBAL_E_MARKET_AVAILABLE,
  GLOBAL_E_MERCHANT_ID,
  GLOBAL_E_URL,
  isWholeSalerWidget,
  MY_ACCOUNT_APP_API_URI,
  ORDER_API_URI,
  POS_MANAGER_APP_API_URI,
  SSR_MODE,
  storageInitSetting,
  VERSION,
} from '@/lib/const';
import { resolveContentDraftModeFromReq } from '@/lib/draftMode';
import { isSsrSafePathname } from '@/lib/experiments/cacheablePages';
import { resolveClientIpFromReq } from '@/lib/experiments/clientIp';
import { ACTIVE_EXPERIMENTS } from '@/lib/experiments/experimentRegistry';
import { resolveExperimentsFromReq } from '@/lib/experiments/resolveExperimentsFromReq';
import type { ExperimentAssignments } from '@/lib/experiments/types';
import { resolveGeoCountryFromReq } from '@/lib/geoCountry';
import { isDeferredGtmPathname } from '@/lib/gtm/deferredGtm';
import type { LocaleMessages } from '@/lib/intl';
import { loadMessages } from '@/lib/intl';
import { getRegionFromLocale } from '@/lib/regions';
import { StorageHelper } from '@/lib/storage';
import {
  defaultFlags,
  parseThirdPartyFlagsFromCookie,
  parseThirdPartyFlagsFromQuery,
  type ThirdPartyFlags,
} from '@/lib/thirdPartyFlags';
import { AlertBannerProvider } from '@/providers/AlertBannerProvider/AlertBannerContext';
import { AlgoliaEventProvider } from '@/providers/AlgoliaEventProvider';
import { CommonModalProvider } from '@/providers/CommonModalProvider';
import { ConfigProvider } from '@/providers/ConfigProvider/ConfigProvider';
import { ErrorProvider } from '@/providers/ErrorProvider/ErrorProvider';
import { ExperimentProvider } from '@/providers/ExperimentProvider';
import { GlobalEProvider } from '@/providers/GlobalEProvider/GlobalEProviderContext';
import { GoogleAnalyticsProvider } from '@/providers/GoogleAnalyticsProvider/GoogleAnalyticsProvider';
import { LocaleContext } from '@/providers/LocaleProvider';
import { ProcessingProvider } from '@/providers/ProcessingProvider/ProcessingProviderContext';
import { RegionProvider } from '@/providers/RegionProvider';
import {
  ThirdPartyFlagProvider,
  useCampaignTrackerEnabled,
  useGtmEnabled,
  useOneTrustEnabled,
} from '@/providers/ThirdPartyFlagsProvider/ThirdPartyFlagsContext';
import { GlobalStyle } from '@/theme';
import { theme } from '@/theme/getTheme';

const GatedPersonalAide = dynamic(
  () =>
    import('@/components/PersonalAide/GatedPersonalAide').then(
      (mod) => mod.GatedPersonalAide,
    ),
  { ssr: false },
);
const NotifyMeModal = dynamic(() =>
  import('@/views/WatchView/components/NotifyMeModal').then(
    (mod) => mod.NotifyMeModal,
  ),
);
const WarrantyExtensionModal = dynamic(() =>
  import('@/components/Cms/Event/WarrantyExtensionModal').then(
    (mod) => mod.WarrantyExtensionModal,
  ),
);

if (!SSR_MODE) {
  window.version = VERSION;
}

StorageHelper.init(storageInitSetting);

export type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
  currentLocale: string;
  currentUrl: string;
  draftMode: boolean;
  fallbackMessages: LocaleMessages;
  initialAssignments: ExperimentAssignments;
  initialThirdPartyFlags: ThirdPartyFlags;
};

const GTM_ID = process.env.NEXT_PUBLIC_GTM_ID as string;
const EDGE_EXPERIMENT_CACHE_VARIANTS_ENABLED =
  process.env.NEXT_PUBLIC_EXPERIMENT_EDGE_CACHE_VARIANTS_ENABLED === 'true';

const AriaLabelsInitializer = () => {
  useAriaLabels();
  return null;
};

const SearchInsightsInit = () => {
  useSearchInsights();
  return null;
};

const GatedCampaignTracker = () => {
  const isEnabled = useCampaignTrackerEnabled();
  return isEnabled ? <CampaignTrackerInit /> : null;
};

const GatedThirdPartyScripts = ({
  currentUrl,
  showCookiesModal,
}: {
  currentUrl: string;
  showCookiesModal: boolean;
}) => {
  const isGtmEnabled = useGtmEnabled();
  const isOneTrustEnabled = useOneTrustEnabled();
  const { asPath } = useRouter();
  return (
    <>
      {GTM_ID &&
        !isWholeSalerWidget(currentUrl) &&
        isGtmEnabled &&
        (isDeferredGtmPathname(asPath) ? (
          <DeferredGoogleTagManager gtmId={GTM_ID} />
        ) : (
          <GoogleTagManager gtmId={GTM_ID} />
        ))}
      {showCookiesModal && isOneTrustEnabled && <OneTrustScript />}
      {showCookiesModal && isOneTrustEnabled && <OneTrustScriptReloader />}
    </>
  );
};

function MyApp({
  Component,
  currentLocale,
  currentUrl,
  draftMode,
  fallbackMessages,
  initialAssignments,
  initialThirdPartyFlags,
  pageProps,
}: AppPropsWithLayout) {
  const [updateUrl, setUpdateUrl] = useState<string>('');
  const { asPath } = useRouter();
  useBuildIdGuard();

  useEffect(() => {
    if (currentUrl !== asPath) {
      setUpdateUrl(
        `${removeTrailingSlash(
          process.env.NEXT_PUBLIC_URL as string,
        )}${asPath}`,
      );
    }
  }, [asPath, currentUrl, currentLocale]);

  useEffect(() => {
    applyGoogleTranslateDOMPatch();
  }, []);

  const showCookiesModal =
    //!currentUrl?.includes('nocookiemodal=true') &&
    //!currentUrl?.includes('://localhost') &&
    !isWholeSalerWidget(currentUrl);

  const getLayout = Component.getLayout ?? ((page: ReactElement) => page);
  const apolloClient = useApollo(pageProps);

  const contentfulMessages: LocaleMessages = pageProps.localeMessages ?? {};

  const localeMessages: LocaleMessages = {
    ...fallbackMessages,
    ...contentfulMessages,
  };

  if (pageProps.localeConfiguration) {
    // init locale configuration in client
    LocaleConfiguration.getInstance().setStates(pageProps.localeConfiguration);
  }

  const { country, locale } = getRegionFromLocale(currentLocale);
  const contentfulLocale = locale.contentful ?? locale.locale;

  return (
    <ThirdPartyFlagProvider initialFlags={initialThirdPartyFlags}>
      <>
        <Head>
          {BRAND == Brand.Gallet && (
            <meta
              content='QsjGwo_Fs6OGyW3Gb_GJRXdE_t49Tvv6UmH1q-qtHg0'
              name='google-site-verification'
            />
          )}
          <link
            crossOrigin='anonymous'
            href={parseUrlOrigin(API_URI)}
            rel='preconnect'
          />
          <link
            crossOrigin='anonymous'
            href={parseUrlOrigin(ORDER_API_URI)}
            rel='preconnect'
          />
          <link
            crossOrigin='anonymous'
            href={parseUrlOrigin(CONFIGURATION_APP_API_URI)}
            rel='preconnect'
          />
          <link
            crossOrigin='anonymous'
            href={parseUrlOrigin(POS_MANAGER_APP_API_URI)}
            rel='preconnect'
          />
          <link
            crossOrigin='anonymous'
            href={parseUrlOrigin(MY_ACCOUNT_APP_API_URI)}
            rel='preconnect'
          />
          <link href={parseUrlOrigin(API_URI)} rel='dns-prefetch' />
          <link href={parseUrlOrigin(ORDER_API_URI)} rel='dns-prefetch' />
          <link
            href={parseUrlOrigin(CONFIGURATION_APP_API_URI)}
            rel='dns-prefetch'
          />
          <link
            href={parseUrlOrigin(POS_MANAGER_APP_API_URI)}
            rel='dns-prefetch'
          />
          <link
            href={parseUrlOrigin(MY_ACCOUNT_APP_API_URI)}
            rel='dns-prefetch'
          />
          {/* Global-e styles */}
          {currentLocale &&
            GLOBAL_E_MARKET_AVAILABLE?.includes(currentLocale) && (
              <link
                href={`//${GLOBAL_E_URL}/includes/css/${GLOBAL_E_MERCHANT_ID}?countryCode=${country.code}`}
                id='GEPIStyles'
                rel='stylesheet'
              />
            )}
        </Head>
        <FaviconLinks brand={toBrandEnum(BRAND)} />
        <ContentfulLivePreviewProvider
          debugMode={draftMode}
          enableInspectorMode={draftMode}
          enableLiveUpdates={draftMode}
          locale={contentfulLocale || 'en-US'}
          targetOrigin={[
            'https://app.contentful.com',
            'https://app.eu.contentful.com',
          ]}
        >
          <LocaleContext.Provider value={currentLocale}>
            <SEOHead
              requestUrl={updateUrl || currentUrl}
              seoConfig={pageProps.seoConfig as ContentfulSeoConfiguration}
            />
            <GlobalEProvider>
              <SaleorAuthProvider client={saleorAuthClient}>
                <ApolloProvider client={apolloClient}>
                  <RegionProvider messages={localeMessages}>
                    <AriaLabelsInitializer />
                    <SearchInsightsInit />
                    <SkipToContentLink />
                    <AlgoliaEventProvider>
                      <AlertBannerProvider>
                        <ThemeProvider theme={theme}>
                          <SnackbarProvider
                            anchorOrigin={{
                              vertical: 'top',
                              horizontal: 'right',
                            }}
                            maxSnack={3}
                          >
                            <ErrorProvider>
                              <ErrorBoundaryWrapper>
                                <ConfigProvider apolloClient={apolloClient}>
                                  <ProcessingProvider>
                                    <CommonModalProvider
                                      NotifyMeComponent={NotifyMeModal}
                                      WarrantyExtensionComponent={
                                        WarrantyExtensionModal
                                      }
                                    >
                                      <ExperimentProvider
                                        initialAssignments={initialAssignments}
                                      >
                                        <GoogleAnalyticsProvider>
                                          <GatedCampaignTracker />
                                          <GlobalStyle />
                                          <NextProgressWrapper />
                                          <Processing>
                                            {getLayout(
                                              <Component {...pageProps} />,
                                            )}
                                          </Processing>
                                          <div id='modal-root' />
                                          <div id='listing-modal-root' />
                                          <GatedPersonalAide />
                                        </GoogleAnalyticsProvider>
                                      </ExperimentProvider>
                                    </CommonModalProvider>
                                  </ProcessingProvider>
                                </ConfigProvider>
                              </ErrorBoundaryWrapper>
                            </ErrorProvider>
                          </SnackbarProvider>
                        </ThemeProvider>
                      </AlertBannerProvider>
                    </AlgoliaEventProvider>
                  </RegionProvider>
                </ApolloProvider>
              </SaleorAuthProvider>

              {currentLocale &&
                GLOBAL_E_MARKET_AVAILABLE?.includes(currentLocale) && (
                  <GlobalEScript />
                )}
            </GlobalEProvider>
            <GatedThirdPartyScripts
              currentUrl={currentUrl}
              showCookiesModal={showCookiesModal}
            />
            {country?.code === 'AU' && <ZipScript />}
            {country?.code === 'CH' && <YourAssetScript />}
          </LocaleContext.Provider>
        </ContentfulLivePreviewProvider>
      </>
    </ThirdPartyFlagProvider>
  );
}

MyApp.getInitialProps = async (appContext: AppContext) => {
  const appProps = await App.getInitialProps(appContext);
  const ctx: NextPageContext = appContext.ctx;
  const currentLocale = ctx.query.lang as string;
  const currentUrl = extractPageUrlFromNextContext(
    ctx,
    process.env.NEXT_PUBLIC_URL as string,
  );

  const draftMode = resolveContentDraftModeFromReq(ctx.req);

  // MyApp.getInitialProps will run at the same time as ssr, so the locale configuration is still fallback data here.
  const { locale } = getRegionFromLocale(currentLocale);

  // Pre-load aria-labels during SSR so they're cached before components render
  const [fallbackMessages] = await Promise.all([
    loadMessages(locale.code),
    loadAriaLabels(locale.code),
  ]);

  // Parse third-party disable flags server-side. The session cookie is the
  // fallback so the opt-out survives navigation/redirects that drop the query
  // (no first-paint flash before the client provider runs).
  const cookieThirdPartyFlags = parseThirdPartyFlagsFromCookie(
    ctx.req?.headers.cookie,
    defaultFlags,
  );
  const initialThirdPartyFlags = parseThirdPartyFlagsFromQuery(
    ctx.query,
    cookieThirdPartyFlags,
  );

  const urlSearch = ctx.asPath?.includes('?')
    ? ctx.asPath.slice(ctx.asPath.indexOf('?'))
    : '';
  const pathname = ctx.asPath?.split('?')[0];
  const shouldSerializeAssignments =
    EDGE_EXPERIMENT_CACHE_VARIANTS_ENABLED || isSsrSafePathname(pathname);

  const initialAssignments = shouldSerializeAssignments
    ? resolveExperimentsFromReq(ctx.req, ACTIVE_EXPERIMENTS, {
        clientIp: resolveClientIpFromReq(ctx.req, urlSearch),
        geoCountry: resolveGeoCountryFromReq(ctx.req, urlSearch),
        language: currentLocale?.split('-')[1],
        urlSearch,
      })
    : {};

  return {
    ...appProps,
    currentUrl,
    draftMode,
    fallbackMessages,
    currentLocale,
    initialAssignments,
    initialThirdPartyFlags,
  };
};

export default MyApp;
